// Moving a site over from Tidio: reading its export files. Tidio has no single
// "export everything" — contacts come from the Contacts list export, each
// conversation from "Export transcript to .CSV", and the Lyro knowledge base
// as a Question/Answer CSV (plus the website addresses it was trained on).
// Column names vary by locale and plan, so each field accepts several headers.
// Pure; shared by the server (which imports) and the admin UI (which
// previews). See tidio.test.ts.

import { ATTRIBUTE_KEY_RE, ATTRIBUTE_LIMITS, RESERVED_ATTRIBUTE_KEYS } from '../flows';
import { columnIndex, csvToFaq, type CsvImport } from '../knowledge/csv';
import type { FaqPair } from '../knowledge/faq';

/** Per-upload caps: the import runs inside one admin request. */
export const MAX_TIDIO_CONTACTS = 10_000;
export const MAX_TIDIO_MESSAGES = 20_000;
export const MAX_TIDIO_SOURCES = 50;

export interface TidioPerson {
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface TidioContact extends TidioPerson {
  /** ISO time, when the export has a usable creation date. */
  createdAt: string | null;
  /** Every other non-empty column (custom properties), keyed for {{contact.<key>}}. */
  attributes: Record<string, string>;
}

export type TidioAuthor = 'visitor' | 'agent' | 'ai' | 'system';

export interface TidioMessage {
  author: TidioAuthor;
  body: string;
  /** ISO time; null when the row had none we could read. */
  sentAt: string | null;
}

export interface TidioConversation {
  /** Tidio's conversation id when the file has one; otherwise null and the
   *  importer recognises a re-upload by its content. */
  externalId: string | null;
  visitor: TidioPerson;
  messages: TidioMessage[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cleanEmail = (v: string) => (EMAIL_RE.test(v.trim()) ? v.trim().toLowerCase() : null);
const cleanPhone = (v: string) => (/\d{5,}/.test(v.replace(/\D/g, '')) ? v.trim() : null);
const oneLine = (v: string) => v.replace(/\s+/g, ' ').trim();

/**
 * A Tidio date as ISO, or null. Exports write "2024-05-01 13:45:00" (read as
 * UTC: the file carries no zone), "01.05.2024 13:45", ISO strings or Unix
 * seconds/milliseconds.
 */
export function parseTidioDate(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  let ms: number;
  if (/^\d{10}$/.test(v)) ms = Number(v) * 1000;
  else if (/^\d{13}$/.test(v)) ms = Number(v);
  else {
    const dotted = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(v);
    if (dotted) {
      const [, d, mo, y, h = '0', mi = '0', s = '0'] = dotted;
      ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
    } else {
      const local = /^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(v);
      ms = Date.parse(local ? `${v.replace(' ', 'T')}Z` : v);
    }
  }
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** "Lifetime value ($)" → "lifetime_value"; null when nothing usable is left. */
export function attributeKey(header: string): string | null {
  let key = header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/, '');
  if (!key || !ATTRIBUTE_KEY_RE.test(key)) return null;
  if (RESERVED_ATTRIBUTE_KEYS.has(key)) key = `tidio_${key}`;
  return key;
}

const CONTACT_COLUMNS = {
  first: ['first name', 'firstname', 'given name'],
  last: ['last name', 'lastname', 'surname', 'family name'],
  name: ['name', 'full name', 'contact name', 'visitor name'],
  email: ['email', 'e mail', 'email address', 'contact email'],
  phone: ['phone', 'phone number', 'telephone', 'mobile', 'mobile number'],
  created: ['created at', 'created', 'creation date', 'date created', 'first seen', 'signed up'],
} as const;

/** Tidio's Contacts export. A row needs an email or a phone number: that is
 *  how a contact is matched to people already here, and to its conversations. */
export function tidioContacts(rows: string[][]): CsvImport<TidioContact> & { missingHeader?: true } {
  if (rows.length === 0) return { items: [], skipped: [] };
  const header = rows[0]!;
  const idx = Object.fromEntries(
    Object.entries(CONTACT_COLUMNS).map(([k, names]) => [k, columnIndex(header, names)]),
  ) as Record<keyof typeof CONTACT_COLUMNS, number>;
  if (idx.email === -1 && idx.phone === -1) return { items: [], skipped: [], missingHeader: true };
  const known = new Set(Object.values(idx).filter((i) => i !== -1));
  const extra = header
    .map((h, c) => [c, attributeKey(h)] as const)
    .filter((e): e is readonly [number, string] => !known.has(e[0]) && e[1] !== null);

  const items: TidioContact[] = [];
  const skipped: CsvImport<TidioContact>['skipped'] = [];
  rows.slice(1).forEach((r, i) => {
    const cell = (k: keyof typeof CONTACT_COLUMNS) => (idx[k] === -1 ? '' : (r[idx[k]] ?? '').trim());
    const email = cleanEmail(cell('email'));
    const phone = cleanPhone(cell('phone'));
    if (!email && !phone) {
      skipped.push({ row: i + 2, reason: 'needs an email address or phone number' });
      return;
    }
    const name = oneLine(cell('name') || `${cell('first')} ${cell('last')}`) || null;
    const attributes: Record<string, string> = {};
    for (const [c, key] of extra) {
      const value = (r[c] ?? '').trim().slice(0, ATTRIBUTE_LIMITS.valueLength);
      if (value && !(key in attributes) && Object.keys(attributes).length < ATTRIBUTE_LIMITS.count) {
        attributes[key] = value;
      }
    }
    items.push({ name, email, phone, createdAt: parseTidioDate(cell('created')), attributes });
  });
  return { items, skipped };
}

const TRANSCRIPT_COLUMNS = {
  conversation: ['conversation id', 'conversation', 'chat id', 'thread id', 'ticket id'],
  time: ['date', 'time', 'timestamp', 'created at', 'sent at', 'date time', 'datetime', 'message date', 'message time'],
  type: ['author type', 'sender type', 'type', 'role', 'from type', 'user type'],
  author: ['author', 'sender', 'from', 'author name', 'sender name', 'operator'],
  body: ['message', 'content', 'text', 'body', 'message text', 'message content'],
  email: ['visitor email', 'contact email', 'customer email', 'email'],
  name: ['visitor name', 'contact name', 'customer name', 'visitor'],
  phone: ['visitor phone', 'contact phone', 'customer phone', 'phone'],
} as const;

/** Who wrote a message, from Tidio's author type (or, without one, its name). */
export function tidioAuthor(type: string, author: string, visitor: TidioPerson): TidioAuthor {
  const t = type.trim().toLowerCase();
  if (/\b(bot|chatbot|lyro|ai|flow|automation|assistant)\b/.test(t)) return 'ai';
  if (/\b(operator|agent|admin|staff|support|team)\b/.test(t)) return 'agent';
  if (/\b(visitor|contact|customer|client|guest|user)\b/.test(t)) return 'visitor';
  if (/\b(system|event|note|info)\b/.test(t)) return 'system';
  const a = author.trim().toLowerCase();
  if (!a) return 'visitor';
  if (/\b(bot|chatbot|lyro|tidio ai)\b/.test(a)) return 'ai';
  if (a === 'visitor' || a === visitor.name?.toLowerCase() || a === visitor.email) return 'visitor';
  return 'agent';
}

/**
 * Transcript CSVs: one conversation per file, or several in one file told
 * apart by a conversation id column. Messages keep their file order; rows
 * without text (attachments, typing events) are skipped.
 */
export function tidioTranscripts(rows: string[][]): CsvImport<TidioConversation> & { missingHeader?: true } {
  if (rows.length === 0) return { items: [], skipped: [] };
  const header = rows[0]!;
  const idx = Object.fromEntries(
    Object.entries(TRANSCRIPT_COLUMNS).map(([k, names]) => [k, columnIndex(header, names)]),
  ) as Record<keyof typeof TRANSCRIPT_COLUMNS, number>;
  if (idx.body === -1) return { items: [], skipped: [], missingHeader: true };

  const byKey = new Map<string, TidioConversation>();
  const skipped: CsvImport<TidioConversation>['skipped'] = [];
  rows.slice(1).forEach((r, i) => {
    const cell = (k: keyof typeof TRANSCRIPT_COLUMNS) => (idx[k] === -1 ? '' : (r[idx[k]] ?? '').trim());
    const id = cell('conversation');
    let conv = byKey.get(id);
    if (!conv) {
      conv = { externalId: id || null, visitor: { name: null, email: null, phone: null }, messages: [] };
      byKey.set(id, conv);
    }
    // The visitor's details may be on every row or only some.
    conv.visitor.name ??= oneLine(cell('name')) || null;
    conv.visitor.email ??= cleanEmail(cell('email'));
    conv.visitor.phone ??= cleanPhone(cell('phone'));
    const body = cell('body');
    if (!body) {
      skipped.push({ row: i + 2, reason: 'no message text' });
      return;
    }
    conv.messages.push({
      author: tidioAuthor(cell('type'), cell('author'), conv.visitor),
      body,
      sentAt: parseTidioDate(cell('time')),
    });
  });
  return { items: [...byKey.values()].filter((c) => c.messages.length > 0), skipped };
}

const URL_COLUMNS = ['url', 'website', 'link', 'source', 'source url', 'address', 'page'];
const QUESTION = ['question', 'questions', 'q', 'query', 'title'];
const ANSWER = ['answer', 'answers', 'a', 'response', 'reply'];
const isUrl = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

/**
 * Lyro's knowledge: Question/Answer rows become Q&A, and a URL column (or a
 * bare list of addresses) becomes website sources. A row may carry either.
 */
export function tidioKnowledge(rows: string[][]): CsvImport<FaqPair> & { urls: string[] } {
  if (rows.length === 0) return { items: [], skipped: [], urls: [] };
  const header = rows[0]!;
  const q = columnIndex(header, QUESTION);
  const a = columnIndex(header, ANSWER);
  const u = columnIndex(header, URL_COLUMNS);
  const urls = new Set<string>();

  if (q === -1 || a === -1) {
    // No Q&A header: a list of addresses, or headerless question,answer rows.
    const bare = rows.filter((r) => r.length >= 1 && isUrl(r[0]!) && r.slice(1).every((c) => !c.trim()));
    if (u !== -1 || bare.length === rows.length) {
      const skipped: CsvImport<FaqPair>['skipped'] = [];
      const start = u !== -1 ? 1 : 0;
      rows.slice(start).forEach((r, i) => {
        const v = (r[u === -1 ? 0 : u] ?? '').trim();
        if (isUrl(v)) urls.add(v);
        else skipped.push({ row: i + start + 1, reason: 'not a website address' });
      });
      return { items: [], skipped, urls: [...urls] };
    }
    return { ...csvToFaq(rows), urls: [] };
  }

  const items: FaqPair[] = [];
  const skipped: CsvImport<FaqPair>['skipped'] = [];
  rows.slice(1).forEach((r, i) => {
    const question = oneLine(r[q] ?? '');
    const answer = (r[a] ?? '').trim();
    const url = u === -1 ? '' : (r[u] ?? '').trim();
    if (isUrl(url)) urls.add(url);
    if (question && answer) items.push({ question, answer });
    else if (!isUrl(url)) skipped.push({ row: i + 2, reason: 'needs both a question and an answer' });
  });
  return { items, skipped, urls: [...urls] };
}
